import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrLoadingState, ClrModal } from '@clr/angular';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { BaseDataRoute, BaseItemsWrapperComponent } from '../../_modals/baseitems-wrapper/baseitems-wrapper.component';
import { ToastrService } from 'ngx-toastr';
import { APIService } from 'src/app/services/APIService/api.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { FormGroupOfType } from 'src/app/types/shared';
import { CustomValidators } from 'src/app/shared/classes/custom-validators';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { FileForPreview } from '../../_shared/preview-thumbnails/preview-thumbnail.type';
import { DBStatus } from 'src/app/types/api';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-gleis-code-verwaltung',
  templateUrl: './gleis-code-verwaltung.component.html',
  styleUrl: './gleis-code-verwaltung.component.scss',
  imports: [
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    MrTranslatePipe,
    TablePrettyPrintPipe,
    TitleCasePipe,
    HideIDColumnsPipe,
    NgTemplateOutlet,
    BaseItemsWrapperComponent,
    PreviewThumbnailsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GleisCodeVerwaltungComponent {
  private readonly api = inject(APIService);
  private readonly confirm = inject(ConfirmService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);

  protected readonly isRep = this.api.isRep;

  public closedialog = new Subject<boolean>();
  readonly bild = viewChild<PreviewThumbnailsComponent>('bild');

  private readonly codesResource = resource({
    loader: () => this.api.getBaseItems('SBFEK'),
    defaultValue: TABLE_EMPTY(),
  });

  protected readonly table = this.codesResource.value;
  private readonly codes = computed(() => this.table().rows.map(row => row.Code));
  protected readonly isLoading = computed(() => this.codesResource.isLoading() || this.selectResource.isLoading());

  private readonly selectResource = rxResource({
    stream: () =>
      forkJoin({
        SSCHF: this.api.getUniqueBaseItems('SSCHF'),
        SSCHW: this.api.getUniqueBaseItems('SSCHW'),
        SBEFT: this.api.getUniqueBaseItems('SBEFT'),
        SEIND: this.api.getUniqueBaseItems('SEIND'),
      }),
    defaultValue: {
      SSCHF: [],
      SSCHW: [],
      SBEFT: [],
      SEIND: [],
    },
  });

  protected readonly schienenformen = computed(() => this.selectResource.value().SSCHF);
  protected readonly schwellenarten = computed(() => this.selectResource.value().SSCHW);
  protected readonly schienenbefestigungen = computed(() => this.selectResource.value().SBEFT);
  protected readonly eindeckungen = computed(() => this.selectResource.value().SEIND);

  protected readonly openedCode = linkedSignal<SBFEK_Item | undefined>(() => (this.table(), undefined));
  protected readonly openedBild = computed<FileForPreview[]>(() => {
    const bild = this.openedCode()?.Bild;
    return bild ? [{ base64: bild }] : [];
  });

  protected readonly isBaseItemModalOpen = signal(false);
  protected readonly editTabelle = signal<BaseDataRoute>(null);
  protected readonly loadState = signal(ClrLoadingState.DEFAULT);

  protected readonly codeForm = new FormGroupOfType<SBFEK_Model>({
    Code: new FormControl<string>(null, { updateOn: 'blur' }), // * Dynamic required with notInTableValidator
    Schienenform: new FormControl<string>(null, Validators.required),
    Schwellenart: new FormControl<string>(null), // * Dynamic required based on Eindeckung
    Schienenbefestigung: new FormControl<string>(null), // * Dynamic required based on Eindeckung
    Eindeckung: new FormControl<string>(null, { updateOn: 'blur' }),
    Geschlossen: new FormControl(false, { nonNullable: true }),
    Kosten: new FormControl<number>(null),
  });

  constructor() {
    effect(() => this.codeForm.reset(this.openedCode() ?? undefined));
    effect(() => {
      const current = this.openedCode()?.Code;
      const list = this.codes();
      const codeCtrl = this.codeForm.controls.Code;
      codeCtrl.setValidators([Validators.required, CustomValidators.NotInTableValidator(list, current)]);
      codeCtrl.updateValueAndValidity();
    });
    this.codeForm.controls.Eindeckung.valueChanges.subscribe(eindeckung => {
      const { Schwellenart, Schienenbefestigung } = this.codeForm.controls;
      if (!eindeckung) {
        Schwellenart.setValidators(Validators.required);
        Schienenbefestigung.setValidators(Validators.required);
      } else {
        Schwellenart.clearValidators();
        Schienenbefestigung.clearValidators();
      }
      Schwellenart.updateValueAndValidity();
      Schienenbefestigung.updateValueAndValidity();
    });
  }

  protected async saveCode(modal: ClrModal) {
    this.loadState.set(ClrLoadingState.LOADING);
    try {
      const bild = (await this.bild().packAllFiles())[0];

      const sendObj: SBFEK_Item = {
        ID: this.openedCode()?.ID,
        ...this.codeForm.getRawValue(),
        Bild: bild?.base64,
      };

      await this.api.setBaseItems('SBFEK', sendObj);

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.codesResource.reload();
      modal.close();
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteCode() {
    const ok = await this.confirm.danger(
      this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Zeile löschen wollen?')
    );
    if (!ok) return;

    try {
      await this.api.deleteBaseItems('SBFEK', this.openedCode().ID);

      this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.codesResource.reload();
      this.openedCode.set(undefined);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected updateTabelle(list: string[]) {
    this.selectResource.update(select => ((select[this.editTabelle().tabelle] = list), { ...select }));
  }

  public close() {
    this.closedialog.next(true);
  }
}

interface SBFEK_Model {
  Code: string;
  Schienenform: string;
  Schwellenart: string | null;
  Schienenbefestigung: string | null;
  Eindeckung: string | null;
  Kosten: number | null;
  Geschlossen: boolean;
}

export interface SBFEK_Item extends SBFEK_Model, Partial<DBStatus> {
  ID?: number;
  Bild: string | null;
}
